import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../logo.png";
import { auth, googleProvider } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import DarkVeil from "./DarkVeil";
import SplitType from "split-type";
import gsap from "gsap";
import "./Login.css";

const translations = {
  en: {
    createAccount: "Create Account",
    welcomeBack: "Welcome Back",
    emailPlaceholder: "Email",
    passwordPlaceholder: "Password",
    signUp: "Sign Up",
    logIn: "Log In",
    alreadyAccount: "Already have an account?",
    dontHaveAccount: "Don't have an account?",
    continueGoogle: "Continue with Google",
    rememberMe: "Remember Me",
    errors: {
      "auth/invalid-email": "Invalid email address.",
      "auth/user-not-found": "No account found with this email.",
      "auth/wrong-password": "Incorrect password.",
      "auth/email-already-in-use": "This email is already in use.",
      "auth/weak-password": "Password should be at least 6 characters.",
      "auth/popup-closed-by-user": "Google sign-in was cancelled.",
      default: "Something went wrong. Please try again.",
    },
  },
  he: {
    createAccount: "צור חשבון",
    welcomeBack: "ברוך שובך",
    emailPlaceholder: "אימייל",
    passwordPlaceholder: "סיסמה",
    signUp: "הרשמה",
    logIn: "התחברות",
    alreadyAccount: "כבר יש לך חשבון?",
    dontHaveAccount: "אין לך חשבון?",
    continueGoogle: "המשך עם גוגל",
    rememberMe: "זכור אותי",
    errors: {
      "auth/invalid-email": "כתובת אימייל לא תקינה.",
      "auth/user-not-found": "לא נמצא חשבון עם האימייל הזה.",
      "auth/wrong-password": "סיסמה שגויה.",
      "auth/email-already-in-use": "האימייל הזה כבר בשימוש.",
      "auth/weak-password": "הסיסמה צריכה להכיל לפחות 6 תווים.",
      "auth/popup-closed-by-user": "ההתחברות עם גוגל בוטלה.",
      default: "משהו השתבש. נסה שוב.",
    },
  },
};

export default function Login() {
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [message, setMessage] = useState("");
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lang] = useState<"en" | "he">("en");
  const [rememberMe, setRememberMe] = useState(false);
  const t = translations[lang];

  // Load remembered email on mount
  useEffect(() => {
    const rememberedEmail = localStorage.getItem("rememberedEmail");
    if (rememberedEmail) {
      setEmail(rememberedEmail);
      setRememberMe(true);
    }
  }, []);

  // Animated title
  useEffect(() => {
    const target = document.querySelector(".auth-title") as HTMLElement | null;
    if (!target) return;

    target.innerHTML = isSigningUp ? t.createAccount : t.welcomeBack;
    const split = new SplitType(target, { types: "chars" });

    gsap.from(split.chars, {
      opacity: 0,
      y: 25,
      stagger: 0.01,
      duration: 1.2,
      ease: "power3.out",
    });

    return () => {
      split.revert();
    };
  }, [isSigningUp, lang, t.createAccount, t.welcomeBack]);

  const getFriendlyAuthMessage = (code: string): string => {
    return (t.errors as any)[code] || t.errors.default;
  };

  // handleAuth supports being called as form submit or direct click
  const handleAuth = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!email) {
      setMessage("Please enter an email.");
      return;
    }
    if (!isSigningUp && !password) {
      setMessage("Please enter your password.");
      return;
    }

    setLoading(true);
    setMessage("");
    try {
      // Set persistence based on Remember Me
      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );

      if (isSigningUp) {
        await createUserWithEmailAndPassword(auth, email, password);
      } else {
        await signInWithEmailAndPassword(auth, email, password);
      }

      // Save remembered email if requested
      if (rememberMe) {
        localStorage.setItem("rememberedEmail", email);
      } else {
        localStorage.removeItem("rememberedEmail");
      }

      // Redirect into app
      navigate("/app", { replace: true });
    } catch (err: any) {
      console.error("Auth error:", err);
      setMessage(getFriendlyAuthMessage(err.code || err.message || "default"));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setLoading(true);
    setMessage("");
    try {
      await setPersistence(
        auth,
        rememberMe ? browserLocalPersistence : browserSessionPersistence
      );
      await signInWithPopup(auth, googleProvider);

      if (rememberMe && auth.currentUser?.email) {
        localStorage.setItem("rememberedEmail", auth.currentUser.email);
      }

      navigate("/app", { replace: true });
    } catch (err: any) {
      console.error("Google sign-in error:", err);
      setMessage(getFriendlyAuthMessage(err.code || err.message || "default"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="DarkVeil-container">
        <DarkVeil />
      </div>

      <div className="auth-background">
        <div className="auth-wrapper" dir={lang === "he" ? "rtl" : "ltr"}>
          <div className="auth-container" role="main" aria-labelledby="auth-heading">
            <div className="auth-header">
              <img src={logo} alt="App Logo" className="auth-logo" />
              <h2 id="auth-heading" className="auth-title" key={`${lang}-${isSigningUp}`}>
                {isSigningUp ? t.createAccount : t.welcomeBack}
              </h2>
            </div>

            <form
              onSubmit={handleAuth}
              style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center" }}
            >
              <input
                className="email-input-wrapper"
                id="email-input"
                type="email"
                placeholder={t.emailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                aria-label={t.emailPlaceholder}
                autoComplete="email"
                required
                style={{ marginBottom: 12 }}
              />

              <div className="password-input-wrapper" style={{ marginBottom: 12 }}>
                <input
                  id="password-input"
                  type={showPassword ? "text" : "password"}
                  placeholder={t.passwordPlaceholder}
                  className="password-input"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={loading}
                  aria-label={t.passwordPlaceholder}
                  autoComplete={isSigningUp ? "new-password" : "current-password"}
                />
                <button
                  id="password-toggle-button"
                  type="button"
                  className="password-toggle-button"
                  onClick={() => setShowPassword((v) => !v)}
                  disabled={loading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>

              {/* Remember Me Checkbox */}
              <div className="remember-me-wrapper" style={{ marginTop: 8, marginBottom: 16 }}>
                <label style={{ display: "flex", alignItems: "center", gap: "0.5rem", cursor: "pointer", transition: "all 0.2s" }}>
                  <input
                    type="checkbox"
                    checked={rememberMe}
                    onChange={() => setRememberMe((v) => !v)}
                    disabled={loading}
                    aria-checked={rememberMe}
                  />
                  <span style={{ color: "#cbd6ff" }}>{t.rememberMe}</span>
                </label>
              </div>

              <div className="button-row">
                <button
                  className="main-button"
                  type="submit"
                  disabled={loading}
                >
                  {loading ? "Loading..." : isSigningUp ? t.signUp : t.logIn}
                </button>

                <button
                  className="main-button"
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={loading}
                >
                  {loading ? (
                    "Loading..."
                  ) : (
                    <>
                      <img
                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                        alt="Google"
                        style={{ width: 20 }}
                        aria-hidden
                      />
                      <span>{t.continueGoogle}</span>
                    </>
                  )}
                </button>
              </div>
            </form>

            {/* <div className="toggle-row">
              <span>{isSigningUp ? t.alreadyAccount : t.dontHaveAccount}</span>
              <button
                className="toggle-button"
                onClick={() => {
                  setIsSigningUp((s) => !s);
                  setMessage("");
                }}
                disabled={loading}
                aria-pressed={isSigningUp}
              >
                {isSigningUp ? t.logIn : t.signUp}
              </button>
            </div> */}

            {message && <p style={{ color: "#f44336", marginTop: "1rem" }}>{message}</p>}
          </div>
        </div>
      </div>
    </>
  );
}